import { z } from 'zod';
import { ALERT_BANDS, MONITOR_KEYS, MONITOR_MODES, thresholdRefusal } from '../monitors';
import { idempotency } from './spine';

/**
 * `monitor.update` (ops insights Phase 7 spec §7): what the owner chose for one monitor. A key
 * left out is left alone; `bandOverride: null` goes back to the monitor's own band. The
 * thresholds are read with the monitor's OWN schema (`MONITOR_CATALOG[key].thresholds`), the one
 * its evaluator reads the row with, so nothing the check would refuse can be saved.
 */
export const monitorUpdateInput = idempotency
  .extend({
    key: z.enum(MONITOR_KEYS),
    enabled: z.boolean().optional(),
    mode: z.enum(MONITOR_MODES).optional(),
    bandOverride: z.enum(ALERT_BANDS).nullable().optional(),
    thresholds: z.record(z.string(), z.unknown()).optional(),
  })
  .superRefine((v, ctx) => {
    if (
      v.enabled === undefined &&
      v.mode === undefined &&
      v.bandOverride === undefined &&
      v.thresholds === undefined
    ) {
      ctx.addIssue({ code: 'custom', message: 'Change at least one thing' });
    }
    if (v.thresholds !== undefined) {
      // The Settings form asks the same question before it sends: one sentence, both places.
      const refusal = thresholdRefusal(v.key, v.thresholds);
      if (refusal !== null) {
        ctx.addIssue({ code: 'custom', path: ['thresholds'], message: refusal });
      }
    }
  });
export type MonitorUpdateInput = z.infer<typeof monitorUpdateInput>;

/**
 * `monitor.review` (Phase 7 spec §7): one monitor's testing review, everything it raised in the
 * last `ALERT_REVIEW_DAYS` days.
 */
export const monitorReviewInput = z.object({ key: z.enum(MONITOR_KEYS) });
export type MonitorReviewInput = z.infer<typeof monitorReviewInput>;
